import { skipToken } from "@reduxjs/toolkit/query";
import {
  ChapterStatus,
  type TrainingParticipantChapter,
  type TrainingParticipantChapterAssessment,
} from "@repo/app-types/entities/trainingParticipant";
import { Size } from "@repo/ui/helpers/helpers";
import { pillStyle } from "@repo/ui/Pills/pill";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC } from "react";
import { Button } from "react-aria-components";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { SizedContainer } from "../../components/sizedContainer.js";
import { useChapterPassRate } from "./chapterAssessmentHelpers.js";
import { chapterStatusIcons } from "./chapterHelpers.js";
import { ChapterTooltip } from "./chapterTooltip.js";
import { ChapterTooltipScoreLine } from "./chapterTooltipScoreLine.js";
import { ChapterTooltipStatusLine } from "./chapterTooltipStatusLine.js";
import { TrainingAssessmentTemplateScorePill } from "./trainingAssessmentTemplateScorePill.js";

export interface AssessmentCellChapterScoreProps {
  chapterIndex: number;
  chapter: TrainingParticipantChapter;
  assessment: TrainingParticipantChapterAssessment | undefined;
  onClick: () => void;
}

export const AssessmentCellChapterScore: FC<AssessmentCellChapterScoreProps> = (
  props,
) => {
  const assessmentTemplateAssessment = props.assessment?.assessment_template;
  const playbookId =
    assessmentTemplateAssessment?.recording_assessment_template_id;
  const assessmentTemplateCriteriaGrading =
    assessmentTemplateAssessment?.criteria_grading;

  const { data: playbook, isLoading: isLoadingPlaybook } = usePlaybookByIdQuery(
    playbookId ?? skipToken,
  );

  const passRate = useChapterPassRate(
    playbook,
    assessmentTemplateCriteriaGrading,
  );

  return (
    <TooltipTrigger closeDelay={150}>
      {props.chapter.status === ChapterStatus.Completed ? (
        <TrainingAssessmentTemplateScorePill
          size={Size.SMALL}
          score={passRate}
          isLoading={isLoadingPlaybook}
          onClick={props.onClick}
        />
      ) : (
        <Button
          className={pillStyle({
            isClickable: true,
            size: Size.SMALL,
            className: "group ring-transparent hover:bg-gray-500/10",
          })}
          onPress={props.onClick}
        >
          <SizedContainer className="tabular-nums" childrenForSize="100%">
            <div className="flex h-full w-full items-center justify-center">
              {chapterStatusIcons[props.chapter.status]}
            </div>
          </SizedContainer>
        </Button>
      )}

      <ChapterTooltip chapterIndex={props.chapterIndex} chapter={props.chapter}>
        <ChapterTooltipStatusLine chapter={props.chapter} />
        <ChapterTooltipScoreLine passRate={passRate} />
      </ChapterTooltip>
    </TooltipTrigger>
  );
};
